import {
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Input,
  InputGroup,
  Text,
} from "@chakra-ui/react";
import { useMemo } from "react";
import Select from "react-select";
import { useDropdownOptions } from "../../hooks/useDropdownOptions";

// ─── Shared react-select styles ─────────────────────────────────────────────
const selectStyles = {
  container: (base) => ({ ...base, width: "100%" }),
  control: (base, state) => ({
    ...base,
    minHeight: "40px",
    borderRadius: "8px",
    borderColor: state.isFocused ? "#3182ce" : "#E2E8F0",
    boxShadow: state.isFocused ? "0 0 0 1px #3182ce" : "none",
    fontSize: "14px",
    "&:hover": { borderColor: "#CBD5E0" },
    backgroundColor: "#FAFAFA",
    transition: "all 0.15s",
  }),
  placeholder: (base) => ({ ...base, color: "#A0AEC0", fontSize: "14px" }),
  option: (base, state) => ({
    ...base,
    fontSize: "14px",
    backgroundColor: state.isSelected
      ? "#EBF8FF"
      : state.isFocused
        ? "#F7FAFC"
        : "white",
    color: state.isSelected ? "#2B6CB0" : "#2D3748",
    fontWeight: state.isSelected ? "500" : "normal",
  }),
  singleValue: (base) => ({ ...base, color: "#2D3748", fontSize: "14px" }),
  menu: (base) => ({
    ...base,
    borderRadius: "8px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
    border: "1px solid #E2E8F0",
    overflow: "hidden",
  }),
  clearIndicator: (base) => ({
    ...base,
    color: "#A0AEC0",
    "&:hover": { color: "#718096" },
  }),
  dropdownIndicator: (base) => ({ ...base, color: "#A0AEC0" }),
};

// ─── Field wrapper ───────────────────────────────────────────────────────────
const Field = ({ label, children }) => (
  <FormControl>
    <FormLabel
      mb={1.5}
      fontSize="xs"
      fontWeight="600"
      textTransform="uppercase"
      letterSpacing="0.06em"
      color="gray.500"
    >
      <Text as="span">{label}</Text>
    </FormLabel>
    {children}
  </FormControl>
);

// ─── Shared input props ──────────────────────────────────────────────────────
const inputStyles = {
  size: "md",
  borderRadius: "lg",
  bg: "gray.50",
  border: "1px solid",
  borderColor: "gray.200",
  fontSize: "sm",
  color: "gray.700",
  _placeholder: { color: "gray.400" },
  focusBorderColor: "blue.400",
  _hover: { borderColor: "gray.300" },
  _focus: { bg: "white" },
  transition: "all 0.15s",
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
const formatDateForInput = (dateString) => {
  if (!dateString) return "";
  if (dateString.includes("-")) return dateString;
  if (dateString.length === 8) {
    return `${dateString.slice(0, 4)}-${dateString.slice(4, 6)}-${dateString.slice(6, 8)}`;
  }
  return "";
};

// ─── Main Component ──────────────────────────────────────────────────────────
const EdssSearchForm = ({
  handleSubmit,
  onSubmit,
  register,
  setValue,
  watch,
  isLoading,
}) => {
  const { options: caseTypeOptions, isLoading: isCaseTypeOptionsLoading } =
    useDropdownOptions("CASE_TYPE");
  const { options: genderOptions, isLoading: isGenderOptionsLoading } =
    useDropdownOptions("GENDER");

  const genderValue = watch("GENDER");
  const productValue = watch("PRODUCT");
  const eventDate = watch("EVENT_DATE");
  const birthDate = watch("BIRTH_DATE");

  const selectedGender = useMemo(
    () => genderOptions?.find((o) => o.value === genderValue) || null,
    [genderValue, genderOptions],
  );

  const selectedProduct = useMemo(
    () => caseTypeOptions?.find((o) => o.value === productValue) || null,
    [productValue, caseTypeOptions],
  );

  return (
    <Box
      as="form"
      onSubmit={handleSubmit(onSubmit)}
      bg="white"
      borderRadius="xl"
      border="1px solid"
      borderColor="gray.200"
      boxShadow="sm"
      overflow="hidden"
    >
      {/* Header */}
      <Box
        px={5}
        py={4}
        borderBottom="1px solid"
        borderColor="gray.100"
        bg="gray.50"
      >
        <HStack spacing={2}>
          <Text
            fontSize="sm"
            fontWeight="700"
            color="gray.700"
            letterSpacing="-0.01em"
          >
            Person Info
          </Text>
        </HStack>
        <Text fontSize="xs" color="gray.400" mt={0.5}>
          Enter one or more fields to filter results
        </Text>
      </Box>

      {/* Form Body */}
      <Box px={5} py={5}>
        <Grid templateColumns="1fr 1fr" gap={4}>
          {/* First Name */}
          <GridItem>
            <Field label="First Name">
              <InputGroup>
                <Input
                  placeholder="e.g. John"
                  {...inputStyles}
                  {...register("FIRST_NAME")}
                />
              </InputGroup>
            </Field>
          </GridItem>

          {/* Last Name */}
          <GridItem>
            <Field label="Last Name">
              <InputGroup>
                <Input
                  placeholder="e.g. Doe"
                  {...inputStyles}
                  {...register("LAST_NAME")}
                />
              </InputGroup>
            </Field>
          </GridItem>

          {/* Case ID */}
          <GridItem colSpan={2}>
            <Field label="Case ID">
              <InputGroup>
                <Input
                  placeholder="Enter case ID"
                  {...inputStyles}
                  {...register("id")}
                />
              </InputGroup>
            </Field>
          </GridItem>

          {/* Gender */}
          <GridItem>
            <Field label="Gender">
              <Select
                styles={selectStyles}
                options={genderOptions}
                value={selectedGender}
                placeholder="Select..."
                isClearable
                isLoading={isGenderOptionsLoading}
                onChange={(opt) => setValue("GENDER", opt?.value || "")}
              />
            </Field>
          </GridItem>

          {/* Disease */}
          <GridItem>
            <Field label="Disease">
              <Select
                styles={selectStyles}
                options={caseTypeOptions}
                value={selectedProduct}
                placeholder="Search disease..."
                isSearchable
                isClearable
                isLoading={isCaseTypeOptionsLoading}
                onChange={(opt) => setValue("PRODUCT", opt?.value || "")}
                noOptionsMessage={() => "No match found"}
              />
            </Field>
          </GridItem>

          {/* Divider */}
          <GridItem colSpan={2}>
            <Divider borderColor="gray.100" />
          </GridItem>

          {/* Event Date */}
          <GridItem>
            <Field label="Event Date">
              <InputGroup>
                <Input
                  type="date"
                  {...inputStyles}
                  {...register("EVENT_DATE")}
                  defaultValue={formatDateForInput(eventDate)}
                />
              </InputGroup>
            </Field>
          </GridItem>

          {/* Date of Birth */}
          <GridItem>
            <Field label="Date of Birth">
              <InputGroup>
                <Input
                  type="date"
                  {...inputStyles}
                  {...register("BIRTH_DATE")}
                  defaultValue={formatDateForInput(birthDate)}
                />
              </InputGroup>
            </Field>
          </GridItem>
        </Grid>
      </Box>

      {/* Footer / Submit */}
      <Box px={5} pb={5}>
        <Button
          type="submit"
          isLoading={isLoading}
          loadingText="Searching..."
          w="full"
          size="md"
          borderRadius="lg"
          colorScheme="blue"
          fontWeight="600"
          fontSize="sm"
          letterSpacing="0.01em"
          boxShadow="sm"
          _hover={{ transform: "translateY(-1px)", boxShadow: "md" }}
          _active={{ transform: "translateY(0)" }}
          transition="all 0.15s"
        >
          Search
        </Button>
      </Box>
    </Box>
  );
};

export default EdssSearchForm;
